import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { supabase } from '../../../supabase/supabase.client';
import { ToastService } from '../../../shared/toast/toast.service';
import { Currency, UploadedImage, PropertyService } from '../../../services/property.service';
import { RichTextEditorComponent } from '../../../shared/rich-text-editor/rich-text-editor.component';

@Component({
  selector: 'app-property-listing-form',
  standalone: true,
  imports: [CommonModule, FormsModule, RichTextEditorComponent],
  templateUrl: './property-listing-form.component.html',
  styleUrl: './property-listing-form.component.css'
})
export class PropertyListingFormComponent implements OnInit {
  // Basic Info
  title = signal('');
  description = signal('');
  propertyType = signal('Apartment');
  listingType = signal('sale');

  // Location
  addressLine1 = signal('');
  addressLine2 = signal('');
  city = signal('');
  state = signal('');
  postalCode = signal('');
  country = signal('');

  // Optional address field visibility
  showAddressLine1 = signal(false);
  showAddressLine2 = signal(false);
  showState = signal(false);
  showPostalCode = signal(false);

  // Details
  bedrooms = signal(0);
  bathrooms = signal(0);
  area = signal(0);
  floor = signal(0);

  // Pricing
  price = signal(0);
  currency = signal('USD');

  // Status
  isPublished = signal(true);
  addToHomepage = signal(false);

  // Building Information
  yearBuilt = signal<number | null>(null);
  buildingStanding = signal('');
  buildingFloors = signal<number | null>(null);
  apartmentsPerFloor = signal<number | null>(null);
  hasElevator = signal(false);
  hasGenerator = signal(false);
  neighborhood = signal('');

  // Area Information
  habitableAreaM2 = signal<number | null>(null);
  landAreaM2 = signal<number | null>(null);
  usableAreaM2 = signal<number | null>(null);
  totalAreaM2 = signal<number | null>(null);
  ceilingHeightM = signal<number | null>(null);

  // Property Features
  isDuplex = signal(false);
  hasTerrace = signal(false);
  hasGarden = signal(false);
  hasView = signal(false);
  isAdvertised = signal(true);

  // Room Information
  livingRoom = signal<number | null>(null);
  livingDining = signal<number | null>(null);
  masterBedrooms = signal<number | null>(null);
  guestBathrooms = signal<number | null>(null);
  maidBathrooms = signal<number | null>(null);
  maidRooms = signal<number | null>(null);
  officeRooms = signal<number | null>(null);

  // Amenities
  antiSeismic = signal(false);
  acSplit = signal(false);
  acCentral = signal(false);
  oilHeating = signal(false);
  fireplace = signal(false);
  americanKitchen = signal(false);
  fittedKitchen = signal(false);
  kitchenette = signal(false);
  doubleGlazing = signal(false);
  electricShutters = signal(false);
  concierge = signal(false);
  videophone = signal(false);
  isFurnished = signal(false);

  // Condition
  generalCondition = signal<string | null>(null);
  needsRenovation = signal(false);

  // Parking & Storage
  parkingCovered = signal<number | null>(null);
  parkingOutdoor = signal<number | null>(null);
  cellar = signal(false);

  // Floor Types
  groundFloor = signal(false);
  mezzanine = signal(false);
  basement = signal(false);

  // Location
  zone = signal('');
  googleMapUrl = signal('');

  // Images
  uploadedImages = signal<UploadedImage[]>([]);

  // UI State
  currencies = signal<Currency[]>([]);
  loading = signal(false);
  isEditMode = signal(false);
  propertyId = signal<string | null>(null);
  isDragging = signal(false);
  maxYear = new Date().getFullYear() + 5;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private propertyService: PropertyService
  ) {}

  /**
   * Validates a storage path to prevent path traversal attacks.
   * Only allows alphanumeric characters, hyphens, underscores, and forward slashes.
   * @param path The storage path to validate
   * @returns true if valid, false otherwise
   */
  private isValidStoragePath(path: string): boolean {
    if (!path || typeof path !== 'string') return false;
    
    // Reject paths with traversal sequences
    if (path.includes('..') || path.includes('\\') || path.includes('\0')) {
      return false;
    }
    
    // Reject paths starting with /
    if (path.startsWith('/')) {
      return false;
    }
    
    // Only allow alphanumeric, hyphens, underscores, dots, and forward slashes
    const validPathPattern = /^[a-zA-Z0-9/_.\-]+$/;
    return validPathPattern.test(path);
  }

  ngOnInit() {
    this.loadCurrencies();
    
    // Check if we're in edit mode
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.isEditMode.set(true);
        this.propertyId.set(id);
        this.loadProperty(id);
      }
    });
  }

  private async loadProperty(propertyId: string) {
    try {
      this.loading.set(true);
      
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          property_images (
            id,
            property_id,
            storage_path,
            alt_text,
            is_primary,
            position
          )
        `)
        .eq('id', propertyId)
        .single();

      if (error) {
        throw new Error(`Failed to load property: ${error.message}`);
      }

      // Populate form fields
      this.title.set(data.title || '');
      this.description.set(data.description || '');
      this.propertyType.set(data.property_type || 'Apartment');
      this.listingType.set(data.listing_type || 'sale');
      this.addressLine1.set(data.address_line1 || '');
      this.addressLine2.set(data.address_line2 || '');
      this.city.set(data.city || '');
      this.state.set(data.state || '');
      this.postalCode.set(data.postal_code || '');
      this.country.set(data.country || '');
      this.bedrooms.set(data.bedrooms || 0);
      this.bathrooms.set(data.bathrooms || 0);
      this.area.set(data.area || 0);
      this.floor.set(data.floor || 0);
      this.price.set(data.price || 0);
      this.currency.set(data.currency || 'USD');
      this.isPublished.set(data.is_published ?? true);

      // Building Information
      this.yearBuilt.set(data.year_built ?? null);
      this.buildingStanding.set(data.building_standing || '');
      this.buildingFloors.set(data.building_floors ?? null);
      this.apartmentsPerFloor.set(data.apartments_per_floor ?? null);
      this.hasElevator.set(data.has_elevator ?? false);
      this.hasGenerator.set(data.has_generator ?? false);
      this.neighborhood.set(data.neighborhood || '');

      // Area Information
      this.habitableAreaM2.set(data.habitable_area_m2 ?? null);
      this.landAreaM2.set(data.land_area_m2 ?? null);
      this.usableAreaM2.set(data.usable_area_m2 ?? null);
      this.totalAreaM2.set(data.total_area_m2 ?? null);
      this.ceilingHeightM.set(data.ceiling_height_m ?? null);

      // Property Features
      this.isDuplex.set(data.is_duplex ?? false);
      this.hasTerrace.set(data.has_terrace ?? false);
      this.hasGarden.set(data.has_garden ?? false);
      this.hasView.set(data.has_view ?? false);
      this.isAdvertised.set(data.is_advertised ?? true);

      // Room Information
      this.livingRoom.set(data.living_room ?? null);
      this.livingDining.set(data.living_dining ?? null);
      this.masterBedrooms.set(data.master_bedrooms ?? null);
      this.guestBathrooms.set(data.guest_bathrooms ?? null);
      this.maidBathrooms.set(data.maid_bathrooms ?? null);
      this.maidRooms.set(data.maid_rooms ?? null);
      this.officeRooms.set(data.office_rooms ?? null);

      // Amenities
      this.antiSeismic.set(data.anti_seismic ?? false);
      this.acSplit.set(data.ac_split ?? false);
      this.acCentral.set(data.ac_central ?? false);
      this.oilHeating.set(data.oil_heating ?? false);
      this.fireplace.set(data.fireplace ?? false);
      this.americanKitchen.set(data.american_kitchen ?? false);
      this.fittedKitchen.set(data.fitted_kitchen ?? false);
      this.kitchenette.set(data.kitchenette ?? false);
      this.doubleGlazing.set(data.double_glazing ?? false);
      this.electricShutters.set(data.electric_shutters ?? false);
      this.concierge.set(data.concierge ?? false);
      this.videophone.set(data.videophone ?? false);
      this.isFurnished.set(data.is_furnished ?? false);

      // Condition
      this.generalCondition.set(data.general_condition ?? null);
      this.needsRenovation.set(data.needs_renovation ?? false);

      // Parking & Storage
      this.parkingCovered.set(data.parking_covered ?? null);
      this.parkingOutdoor.set(data.parking_outdoor ?? null);
      this.cellar.set(data.cellar ?? false);

      // Floor Types
      this.groundFloor.set(data.ground_floor ?? false);
      this.mezzanine.set(data.mezzanine ?? false);
      this.basement.set(data.basement ?? false);

      // Location
      this.zone.set(data.zone || '');
      this.googleMapUrl.set(data.google_map_url || '');
      
      if (data.add_to_homepage !== undefined) {
        this.addToHomepage.set(data.add_to_homepage);
      }

      // Load existing images
      if (data.property_images && data.property_images.length > 0) {
        const images: UploadedImage[] = [];
        for (const imgData of data.property_images) {
          try {
            // Validate storage path to prevent path traversal attacks
            if (!this.propertyService.isValidStoragePath(imgData.storage_path)) {
              console.error('Invalid storage path rejected:', imgData.storage_path);
              continue;
            }
            
            // Generate signed URL for the image
            const { data: signedData } = await supabase.storage
              .from('property_images')
              .createSignedUrl(imgData.storage_path, 604800);

            if (signedData?.signedUrl) {
              images.push({
                id: imgData.id, // Store the image ID for updates
                file: new File([], ''), // Placeholder, won't be uploaded
                preview: signedData.signedUrl,
                alt_text: imgData.alt_text || '',
                is_primary: imgData.is_primary || false,
                position: imgData.position || 0
              });
            }
          } catch (err) {
            console.error('Error loading image:', err);
          }
        }
        this.uploadedImages.set(images);
      }
    } catch (err: any) {
      this.toastService.error(err.message || 'Failed to load property');
      console.error('Error:', err);
    } finally {
      this.loading.set(false);
    }
  }

  onFilesSelected(event: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    console.log(`Processing ${files.length} file(s)`);
    let validCount = 0;
    let errorMessages: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        errorMessages.push(`"${file.name}" is not a valid image file (MIME type: ${file.type || 'unknown'})`);
        continue;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        errorMessages.push(`"${file.name}" exceeds the 5MB file size limit`);
        continue;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const images = this.uploadedImages();
        const newImage: UploadedImage = {
          file: file,
          preview: e.target.result,
          alt_text: '',
          is_primary: images.length === 0, // First image is primary
          position: images.length
        };
        this.uploadedImages.set([...images, newImage]);
      };
      reader.readAsDataURL(file);
      validCount++;
    }

    // Show error messages if any files failed validation
    if (errorMessages.length > 0) {
      const errorMessage = errorMessages.length === 1 
        ? errorMessages[0] 
        : `${errorMessages.length} files failed validation: ${errorMessages.join('; ')}`;
      this.toastService.error(errorMessage);
    }

    // Show success message if any files were accepted
    if (validCount > 0) {
      this.toastService.success(`${validCount} image${validCount > 1 ? 's' : ''} added successfully`);
    }

    // Reset file input
    event.target.value = '';
  }

  removeImage(index: number) {
    const images = this.uploadedImages();
    images.splice(index, 1);

    // Update positions and ensure one primary image
    images.forEach((img, idx) => {
      img.position = idx;
      if (idx === 0 && !images.some(i => i.is_primary)) {
        img.is_primary = true;
      }
    });

    this.uploadedImages.set([...images]);
    this.toastService.success('Image removed');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      console.log(`Dropped ${files.length} file(s)`);
      
      // Convert FileList to Array and process each file
      const fileArray = Array.from(files);
      let validCount = 0;
      let errorMessages: string[] = [];

      fileArray.forEach((file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          errorMessages.push(`"${file.name}" is not a valid image file`);
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          errorMessages.push(`"${file.name}" exceeds the 5MB file size limit`);
          return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const images = this.uploadedImages();
          const newImage: UploadedImage = {
            file: file,
            preview: e.target.result,
            alt_text: '',
            is_primary: images.length === 0, // First image is primary
            position: images.length
          };
          this.uploadedImages.set([...images, newImage]);
        };
        reader.readAsDataURL(file);
        validCount++;
      });

      // Show error messages if any files failed validation
      if (errorMessages.length > 0) {
        const errorMessage = errorMessages.length === 1 
          ? errorMessages[0] 
          : `${errorMessages.length} files failed validation: ${errorMessages.join('; ')}`;
        this.toastService.error(errorMessage);
      }

      // Show success message if any files were accepted
      if (validCount > 0) {
        this.toastService.success(`${validCount} image${validCount > 1 ? 's' : ''} added successfully`);
      }
    }
  }

  setAltText(index: number, altText: string) {
    const images = this.uploadedImages();
    images[index].alt_text = altText;
    this.uploadedImages.set([...images]);
  }

  setPrimary(index: number) {
    const images = this.uploadedImages();
    images.forEach((img, idx) => {
      img.is_primary = idx === index;
    });
    this.uploadedImages.set([...images]);
  }

  private async loadCurrencies() {
    try {
      const { data, error } = await supabase
        .from('currencies')
        .select('code, name, symbol, decimals')
        .order('code');

      if (error) {
        console.error('Error loading currencies:', error);
      } else {
        this.currencies.set((data as Currency[]) || []);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
    }
  }

  async submitListing(): Promise<void> {
    // Comprehensive validation for all required fields
    const requiredFields: { field: string; value: any; errorMessage: string }[] = [
      { field: 'Title', value: this.title(), errorMessage: 'Title is required' },
      { field: 'Description', value: this.description(), errorMessage: 'Description is required' },
      { field: 'Property Type', value: this.propertyType(), errorMessage: 'Property Type is required' },
      { field: 'Listing Type', value: this.listingType(), errorMessage: 'Listing Type (Buy/Rent) is required' },
      { field: 'City', value: this.city(), errorMessage: 'City is required' },
      { field: 'Country', value: this.country(), errorMessage: 'Country is required' },
      { field: 'Bedrooms', value: this.bedrooms(), errorMessage: 'Bedrooms is required' },
      { field: 'Bathrooms', value: this.bathrooms(), errorMessage: 'Bathrooms is required' },
      { field: 'Area', value: this.area(), errorMessage: 'Area is required' },
      { field: 'Price', value: this.price(), errorMessage: 'Price is required' }
    ];

    for (const field of requiredFields) {
      if (!field.value && field.value !== 0) {
        this.toastService.error(field.errorMessage);
        return;
      }
    }

    try {
      this.loading.set(true);

      const listingData = {
        title: this.title(),
        description: this.description() || null,
        property_type: this.propertyType(),
        listing_type: this.listingType(),
        address_line1: this.addressLine1(),
        address_line2: this.addressLine2() || null,
        city: this.city(),
        state: this.state() || null,
        postal_code: this.postalCode() || null,
        country: this.country() || null,
        bedrooms: this.bedrooms() || null,
        bathrooms: this.bathrooms() || null,
        area: this.area() || null,
        floor: this.floor() || null,
        price: this.price(),
        currency: this.currency(),
        is_published: this.isPublished(),
        add_to_homepage: this.addToHomepage(),
        // Building Information
        year_built: this.yearBuilt(),
        building_standing: this.buildingStanding() || null,
        building_floors: this.buildingFloors(),
        apartments_per_floor: this.apartmentsPerFloor(),
        has_elevator: this.hasElevator(),
        has_generator: this.hasGenerator(),
        neighborhood: this.neighborhood() || null,
        // Area Information
        habitable_area_m2: this.habitableAreaM2(),
        land_area_m2: this.landAreaM2(),
        usable_area_m2: this.usableAreaM2(),
        total_area_m2: this.totalAreaM2(),
        ceiling_height_m: this.ceilingHeightM(),
        // Property Features
        is_duplex: this.isDuplex(),
        has_terrace: this.hasTerrace(),
        has_garden: this.hasGarden(),
        has_view: this.hasView(),
        is_advertised: this.isAdvertised(),
        // Room Information
        living_room: this.livingRoom(),
        living_dining: this.livingDining(),
        master_bedrooms: this.masterBedrooms(),
        guest_bathrooms: this.guestBathrooms(),
        maid_bathrooms: this.maidBathrooms(),
        maid_rooms: this.maidRooms(),
        office_rooms: this.officeRooms(),
        // Amenities
        anti_seismic: this.antiSeismic(),
        ac_split: this.acSplit(),
        ac_central: this.acCentral(),
        oil_heating: this.oilHeating(),
        fireplace: this.fireplace(),
        american_kitchen: this.americanKitchen(),
        fitted_kitchen: this.fittedKitchen(),
        kitchenette: this.kitchenette(),
        double_glazing: this.doubleGlazing(),
        electric_shutters: this.electricShutters(),
        concierge: this.concierge(),
        videophone: this.videophone(),
        is_furnished: this.isFurnished(),
        // Condition
        general_condition: this.generalCondition(),
        needs_renovation: this.needsRenovation(),
        // Parking & Storage
        parking_covered: this.parkingCovered(),
        parking_outdoor: this.parkingOutdoor(),
        cellar: this.cellar(),
        // Floor Types
        ground_floor: this.groundFloor(),
        mezzanine: this.mezzanine(),
        basement: this.basement(),
        // Location
        zone: this.zone() || null,
        google_map_url: this.googleMapUrl() || null
      };

      if (this.isEditMode() && this.propertyId()) {
        // Update existing property
        const { error: updateError } = await supabase
          .from('properties')
          .update(listingData)
          .eq('id', this.propertyId());

        if (updateError) {
          throw new Error(`Failed to update listing: ${updateError.message}`);
        }

        // Handle image updates for existing property
        await this.updateImages(this.propertyId()!);

        this.toastService.success('Property listing updated successfully!');
      } else {
        // Create new property
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          this.toastService.error('You must be logged in to create a listing');
          return;
        }

        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .insert({
            ...listingData,
            owner_id: user.id
          })
          .select()
          .single();

        if (propertyError) {
          throw new Error(`Failed to create listing: ${propertyError.message}`);
        }

        // Upload images if any
        if (this.uploadedImages().length > 0) {
          await this.uploadImages(propertyData.id, user.id);
          // Clear cache for this property to ensure updated images are shown
          this.propertyService.clearPropertyImageCache(propertyData.id);
        }

        this.toastService.success('Property listing created successfully!');
        this.resetForm();
      }

      // Navigate after delay in edit mode
      if (this.isEditMode()) {
        // Clear entire cache before navigating to ensure updated images are shown
        this.propertyService.clearImageCache();
        setTimeout(() => {
          this.router.navigate(['/admin/manage-properties'], { 
            queryParams: { refresh: Date.now() } 
          });
        }, 2000);
      }

    } catch (err: any) {
      this.toastService.error(err.message || 'Failed to save listing');
    } finally {
      this.loading.set(false);
    }
  }

  private async uploadImages(propertyId: string, userId: string) {
    const images = this.uploadedImages();
    let uploadedCount = 0;
    let failedCount = 0;

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const timestamp = Date.now();
      
      // Sanitize filename: remove spaces and special characters, keep extension
      const ext = image.file.name.split('.').pop() || 'jpg';
      const cleanFilename = `${timestamp}-${i}.${ext}`;
      
      // Upload directly to bucket root with structure: property_id/filename
      // NOT property_images/property_id/filename
      const uploadPath = `${propertyId}/${cleanFilename}`;
      // Storage path to save in DB: property_id/filename (no prefix)
      const storagePath = uploadPath;

      try {
        console.log(`Uploading image ${i + 1}/${images.length}:`, {
          cleanFilename,
          fileSize: image.file.size,
          fileType: image.file.type,
          uploadPath
        });
        
        // Upload file to storage bucket
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('property_images')
          .upload(uploadPath, image.file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error(`❌ Error uploading image ${i}:`, {
            error: uploadError,
            message: uploadError?.message
          });
          failedCount++;
          continue;
        }

        console.log(`✅ Successfully uploaded image ${i}:`, uploadData);

        // Insert image record in database with storage path (no prefix)
        const { error: dbError, data: dbData } = await supabase
          .from('property_images')
          .insert({
            property_id: propertyId,
            storage_path: storagePath,
            alt_text: image.alt_text || `Image ${i + 1}`,
            is_primary: image.is_primary,
            position: image.position
          });

        if (dbError) {
          console.error(`❌ Error saving image ${i} to database:`, dbError);
          failedCount++;
        } else {
          console.log(`✅ Image ${i} saved to database:`, dbData);
          uploadedCount++;
        }
      } catch (err) {
        console.error(`❌ Error processing image ${i}:`, err);
        failedCount++;
      }
    }

    console.log(`\n📊 Image upload complete: ${uploadedCount} succeeded, ${failedCount} failed`);
    
    if (failedCount > 0) {
      console.warn(`⚠️  ${failedCount} out of ${images.length} images failed to upload`);
    }
  }

  private async updateImages(propertyId: string) {
    const images = this.uploadedImages();

    // Separate existing images (with id) from new images (without id)
    const existingImages = images.filter(img => img.id);
    const newImages = images.filter(img => !img.id);

    // Update existing images: alt_text, is_primary, position
    for (const image of existingImages) {
      try {
        const { error } = await supabase
          .from('property_images')
          .update({
            alt_text: image.alt_text || `Image`,
            is_primary: image.is_primary,
            position: image.position
          })
          .eq('id', image.id);

        if (error) {
          console.error(`❌ Error updating image ${image.id}:`, error);
        } else {
          console.log(`✅ Image ${image.id} updated successfully`);
        }
      } catch (err) {
        console.error(`❌ Error updating image ${image.id}:`, err);
      }
    }

    // Clear cache for this property after updating metadata
    if (existingImages.length > 0) {
      this.propertyService.clearPropertyImageCache(propertyId);
    }

    // Upload new images (file property is non-empty)
    if (newImages.length > 0) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to upload images');
      }

      // Get the user ID for potential use
      const userId = user.id;

      for (let i = 0; i < newImages.length; i++) {
        const image = newImages[i];
        
        // Only upload if file has content
        if (image.file.size === 0) continue;

        const timestamp = Date.now();
        const ext = image.file.name.split('.').pop() || 'jpg';
        const cleanFilename = `${timestamp}-${i}.${ext}`;
        const uploadPath = `${propertyId}/${cleanFilename}`;

        try {
          console.log(`Uploading new image ${i + 1}/${newImages.length}:`, {
            cleanFilename,
            fileSize: image.file.size,
            uploadPath
          });

          // Upload file to storage
          const { error: uploadError, data: uploadData } = await supabase.storage
            .from('property_images')
            .upload(uploadPath, image.file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error(`❌ Error uploading image ${i}:`, uploadError);
            continue;
          }

          console.log(`✅ Successfully uploaded image ${i}:`, uploadData);

          // Insert image record in database
          const { error: dbError } = await supabase
            .from('property_images')
            .insert({
              property_id: propertyId,
              storage_path: uploadPath,
              alt_text: image.alt_text || `Image`,
              is_primary: image.is_primary,
              position: image.position
            });

          if (dbError) {
            console.error(`❌ Error saving image ${i} to database:`, dbError);
          } else {
            console.log(`✅ Image ${i} saved to database`);
          }
        } catch (err) {
          console.error(`❌ Error processing image ${i}:`, err);
        }
      }
    }
  }

  private resetForm(): void {
    this.title.set('');
    this.description.set('');
    this.propertyType.set('Apartment');
    this.listingType.set('sale');
    this.addressLine1.set('');
    this.addressLine2.set('');
    this.city.set('');
    this.state.set('');
    this.postalCode.set('');
    this.country.set('');
    this.bedrooms.set(0);
    this.bathrooms.set(0);
    this.area.set(0);
    this.floor.set(0);
    this.price.set(0);
    this.currency.set('USD');
    this.isPublished.set(true);
    this.addToHomepage.set(false);
    this.uploadedImages.set([]);
    // Building Information
    this.yearBuilt.set(null);
    this.buildingStanding.set('');
    this.buildingFloors.set(null);
    this.apartmentsPerFloor.set(null);
    this.hasElevator.set(false);
    this.hasGenerator.set(false);
    this.neighborhood.set('');
    // Area Information
    this.habitableAreaM2.set(null);
    this.landAreaM2.set(null);
    this.usableAreaM2.set(null);
    this.totalAreaM2.set(null);
    this.ceilingHeightM.set(null);
    // Property Features
    this.isDuplex.set(false);
    this.hasTerrace.set(false);
    this.hasGarden.set(false);
    this.hasView.set(false);
    this.isAdvertised.set(true);
    // Room Information
    this.livingRoom.set(null);
    this.livingDining.set(null);
    this.masterBedrooms.set(null);
    this.guestBathrooms.set(null);
    this.maidBathrooms.set(null);
    this.maidRooms.set(null);
    this.officeRooms.set(null);
    // Amenities
    this.antiSeismic.set(false);
    this.acSplit.set(false);
    this.acCentral.set(false);
    this.oilHeating.set(false);
    this.fireplace.set(false);
    this.americanKitchen.set(false);
    this.fittedKitchen.set(false);
    this.kitchenette.set(false);
    this.doubleGlazing.set(false);
    this.electricShutters.set(false);
    this.concierge.set(false);
    this.videophone.set(false);
    this.isFurnished.set(false);
    // Condition
    this.generalCondition.set(null);
    this.needsRenovation.set(false);
    // Parking & Storage
    this.parkingCovered.set(null);
    this.parkingOutdoor.set(null);
    this.cellar.set(false);
    // Floor Types
    this.groundFloor.set(false);
    this.mezzanine.set(false);
    this.basement.set(false);
    // Location
    this.zone.set('');
    this.googleMapUrl.set('');
  }

  goBack(): void {
    this.router.navigate(['/admin/manage-properties']);
  }
}
